import React from 'react';
import styled from 'styled-components';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

import { validateNoDuplicates, validateEmailSuffix } from 'utils/validators';
import { FjFlexContainer, fjButtonTypes } from 'components/primitives';
import { Button, Input, Checkbox } from 'components/styled-primitives';
import { composeValidators } from 'utils';

import { SuffixListItem } from './suffix-list-item';
import { Label, InputContainer } from '../../../components';

const SuffixInput = styled(Input).attrs({
  placeholder: '@example.com',
})`
  margin-right: ${({ theme: { grid } }) => grid.units(1)};
`;

const SuffixListContainer = styled.div<{ disabled?: boolean }>`
  max-height: ${({ theme: { grid } }) => grid.units(4)};
  overflow-y: scroll;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const FormContainer = styled.form`
  display: flex;
  flex: 1;
`;

// Shape returned by composeValidators (utils is an untyped .js module).
interface CombinedValidator {
  validator: (value: string) => boolean;
  getValidationMessage: (value: string) => string;
}

interface EmailSuffixManagerProps {
  whitelistSuffixes: string[];
  whitelistIgnore: boolean;
  value: string;
  onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSuffixChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  addSuffixToWhitelist: (suffix: string) => void;
  removeSuffixFromWhitelist: (suffix: string) => void;
}

interface EmailSuffixManagerState {
  whitelistSuffixes: string[];
}

class EmailSuffixManager extends React.Component<
  EmailSuffixManagerProps,
  EmailSuffixManagerState
> {
  static defaultProps = {
    value: '',
  };

  suffixContainer: React.RefObject<HTMLDivElement | null>;

  lastSuffix: React.RefObject<HTMLDivElement | null>;

  constructor(props: EmailSuffixManagerProps) {
    super(props);
    this.state = {
      whitelistSuffixes: props.whitelistSuffixes,
    };
    this.suffixContainer = React.createRef();
    this.lastSuffix = React.createRef();
  }

  UNSAFE_componentWillReceiveProps(nextProps: EmailSuffixManagerProps): void {
    if (nextProps.whitelistSuffixes !== this.props.whitelistSuffixes) {
      this.setState(
        {
          whitelistSuffixes: nextProps.whitelistSuffixes,
        },
        () => this.scrollToLastSuffix(),
      );
    }
  }

  getCombinedValidator = (): CombinedValidator =>
    // composeValidators comes from the untyped utils .js module.
    composeValidators(
      validateEmailSuffix,
      validateNoDuplicates(this.props.whitelistSuffixes),
    ) as CombinedValidator;

  getValidationMessage = (value: string): string =>
    this.getCombinedValidator().getValidationMessage(value);

  validator = (value: string): boolean => {
    if (!value) {
      return true;
    }
    return this.getCombinedValidator().validator(value);
  };

  scrollToLastSuffix = (): void => {
    if (!this.lastSuffix.current) return;
    scrollIntoViewIfNeeded(this.lastSuffix.current, {
      duration: 100,
      boundary: this.suffixContainer.current,
    } as Parameters<typeof scrollIntoViewIfNeeded>[1]);
  };

  handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (this.props.value) {
      this.props.addSuffixToWhitelist(this.props.value);
    }
  };

  render(): React.ReactNode {
    const { whitelistIgnore, value, onCheckboxChange, onSuffixChange, removeSuffixFromWhitelist } =
      this.props;
    return (
      <InputContainer>
        <Label>Approved Email Suffixes</Label>
        <Checkbox
          name="ignore-whitelist"
          label="Bypass Whitelist"
          checked={whitelistIgnore}
          onChange={onCheckboxChange}
        />
        <FormContainer onSubmit={this.handleSubmit}>
          <FjFlexContainer direction="column">
            <SuffixInput
              onChange={onSuffixChange}
              value={value}
              validator={this.validator}
              getValidationMessage={this.getValidationMessage}
              disabled={whitelistIgnore}
            />
            <SuffixListContainer ref={this.suffixContainer} disabled={whitelistIgnore}>
              {this.state.whitelistSuffixes.map((suffix, idx) => {
                const isLastSuffix = idx === this.state.whitelistSuffixes.length - 1;
                return (
                  <div key={suffix} ref={isLastSuffix ? this.lastSuffix : () => {}}>
                    <SuffixListItem
                      label={suffix}
                      onRemove={() => removeSuffixFromWhitelist(suffix)}
                    />
                  </div>
                );
              })}
            </SuffixListContainer>
          </FjFlexContainer>
          <Button
            type="submit"
            disabled={!this.validator(value) || whitelistIgnore}
            buttonType={fjButtonTypes.FORM}
          >
            Add
          </Button>
        </FormContainer>
      </InputContainer>
    );
  }
}

export default EmailSuffixManager;
